
type QurvoIdentifyArgs = [userId: string, traits?: Record<string, unknown>];
type QurvoTrackArgs = [event: string, props?: Record<string, unknown>];
type QurvoScreenArgs = [name: string, props?: Record<string, unknown>];

type QueuedCall =
  | { kind: 'identify'; args: QurvoIdentifyArgs }
  | { kind: 'track'; args: QurvoTrackArgs }
  | { kind: 'screen'; args: QurvoScreenArgs };

interface RealQurvo {
  identify: (...args: QurvoIdentifyArgs) => void;
  track: (...args: QurvoTrackArgs) => void;
  screen: (...args: QurvoScreenArgs) => void;
}

let real: RealQurvo | null = null;
const queue: QueuedCall[] = [];

export function bindRealQurvo(instance: RealQurvo): void {
  real = instance;
  while (queue.length > 0) {
    const call = queue.shift()!;
    try {
      switch (call.kind) {
        case 'identify':
          instance.identify(...call.args);
          break;
        case 'track':
          instance.track(...call.args);
          break;
        case 'screen':
          instance.screen(...call.args);
          break;
      }
    } catch (err) {
      console.error('[analyticsQueue] flushed call failed:', err);
    }
  }
}

export const qurvo = {
  identify(...args: QurvoIdentifyArgs): void {
    if (real) real.identify(...args);
    else queue.push({ kind: 'identify', args });
  },
  track(...args: QurvoTrackArgs): void {
    if (real) real.track(...args);
    else queue.push({ kind: 'track', args });
  },
  screen(...args: QurvoScreenArgs): void {
    if (real) real.screen(...args);
    else queue.push({ kind: 'screen', args });
  },
};

let boundStartView: ((name: string) => void) | null = null;
let pendingView: string | null = null;

export function bindDatadogView(startView: (name: string) => void): void {
  boundStartView = startView;
  if (pendingView !== null) {
    try {
      startView(pendingView);
    } catch (err) {
      console.error('[analyticsQueue] datadog startView failed:', err);
    }
    pendingView = null;
  }
}

export const datadogView = {
  start(name: string): void {
    if (boundStartView) boundStartView(name);
    else pendingView = name;
  },
};

let boundSetUser: ((id: string) => void) | null = null;
let boundClearUser: (() => void) | null = null;
let pendingUser: { id: string } | 'cleared' | null = null;

export function bindDatadogUser(handlers: {
  setUser: (id: string) => void;
  clearUser: () => void;
}): void {
  boundSetUser = handlers.setUser;
  boundClearUser = handlers.clearUser;
  if (pendingUser === null) return;
  const queued = pendingUser;
  pendingUser = null;
  try {
    if (queued === 'cleared') handlers.clearUser();
    else handlers.setUser(queued.id);
  } catch (err) {
    console.error('[analyticsQueue] datadog setUser failed:', err);
  }
}

export const datadogUser = {
  set(id: string): void {
    if (!boundSetUser) {
      pendingUser = { id };
      return;
    }
    try {
      boundSetUser(id);
    } catch (err) {
      console.error('[analyticsQueue] datadog setUser failed:', err);
    }
  },
  clear(): void {
    if (!boundClearUser) {
      pendingUser = 'cleared';
      return;
    }
    try {
      boundClearUser();
    } catch (err) {
      console.error('[analyticsQueue] datadog clearUser failed:', err);
    }
  },
};
